/**
 * Contact card used by the Stepping Stones grid view. Encapsulates
 *   - The stone-shaped fit-score badge
 *   - Inline AI deep-dive trigger ("Why this person matters" — 10 credits)
 *   - "Touched today" stamp button (free)
 *   - Delete
 *
 * Insight is hydrated from /api/network/contacts → contact.ai_insight, and
 * lazily refreshed when the analyze button is clicked.
 */
import React, { useState } from "react";
import {
  Sparkles, Trash2, ExternalLink, Loader, ShieldCheck, ArrowRight, CheckCircle2,
} from "lucide-react";
import { api, friendlyAxiosError } from "../../lib/api";
import { daysSinceContact, stalenessTint } from "./utils";

export default function ContactCard({ contact, onChanged, onJumpTo }) {
  const [analyzing, setAnalyzing] = useState(false);
  const [insight, setInsight] = useState(contact.ai_insight || null);
  const [last, setLast] = useState(contact.last_contacted_at || null);
  const [marking, setMarking] = useState(false);
  const [err, setErr] = useState("");

  const days = daysSinceContact(last);
  const tint = stalenessTint(days);

  const analyze = async () => {
    setErr("");
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/network/contacts/${contact.id}/analyze`);
      setInsight(data.insight);
      onChanged?.();
    } catch (e) {
      setErr(friendlyAxiosError(e));
    } finally {
      setAnalyzing(false);
    }
  };

  const markTouched = async () => {
    setErr("");
    setMarking(true);
    try {
      const { data } = await api.post(`/network/contacts/${contact.id}/contacted`);
      setLast(data.last_contacted_at);
      onChanged?.();
    } catch (e) {
      setErr(friendlyAxiosError(e));
    } finally {
      setMarking(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${contact.name}?`)) return;
    try {
      await api.delete(`/network/contacts/${contact.id}`);
      onChanged?.();
    } catch (e) {
      setErr(friendlyAxiosError(e));
    }
  };

  return (
    <div className="rm-card-static p-4 md:p-5" data-testid={`contact-card-${contact.id}`}>
      <div className="flex items-start gap-3">
        {/* Stepping-stone visual — fit score is *the* number on the stone */}
        <div className="flex-shrink-0 relative">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center font-display text-lg"
            style={{
              background: insight ? tint.color : "#FFFBEB",
              color: insight ? "white" : "#0F1B2D",
              border: "3px solid #0F1B2D",
              boxShadow: "2px 2px 0 0 #0F1B2D",
            }}
            title={insight ? `Fit ${insight.fit_score}/100 · ${tint.label}` : "Not analysed yet"}
          >
            {insight?.fit_score ?? contact.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
          </div>
          {/* Staleness pulse dot */}
          {last && (
            <span
              className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full"
              style={{ background: tint.color, border: "2px solid white" }}
              title={tint.label}
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="font-display text-base uppercase leading-tight">{contact.name}</div>
          <div className="text-xs mt-0.5" style={{ color: "#475569" }}>
            {contact.role || "—"}{contact.company ? ` · ${contact.company}` : ""}
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
            {contact.linkedin_url && (
              <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer"
                 className="text-[11px] inline-flex items-center gap-1"
                 style={{ color: "#FF6A00" }}>
                LinkedIn <ExternalLink size={10} />
              </a>
            )}
            {contact.source === "linkedin_csv" && (
              <span className="font-mono-rm text-[9px] uppercase tracking-widest"
                    style={{ color: "#94A3B8" }}>· from your csv</span>
            )}
            {last && (
              <span className="font-mono-rm text-[10px] uppercase tracking-widest"
                    style={{ color: tint.color, fontWeight: 700 }}
                    data-testid={`contact-staleness-${contact.id}`}>
                · {tint.label}
              </span>
            )}
          </div>
        </div>

        <button onClick={remove} className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
                data-testid={`contact-delete-${contact.id}`} title="Remove">
          <Trash2 size={12} />
        </button>
      </div>

      {insight ? (
        <div className="mt-3 space-y-2 text-sm">
          <div>
            <div className="rm-label" style={{ color: "#FF6A00" }}>Why they matter</div>
            <p className="text-sm">{insight.why_matters}</p>
          </div>
          {insight.first_ask && (
            <div>
              <div className="rm-label" style={{ color: "#065F46" }}>Your highest-leverage ask</div>
              <p className="text-sm italic">"{insight.first_ask}"</p>
            </div>
          )}
          {(insight.reciprocity_moves || []).length > 0 && (
            <div>
              <div className="rm-label" style={{ color: "#0F1B2D" }}>How you can help them back</div>
              <ul className="text-sm space-y-0.5 list-none">
                {insight.reciprocity_moves.map((m, i) => (
                  <li key={i} className="flex gap-1.5">
                    <ShieldCheck size={12} className="flex-shrink-0 mt-1" style={{ color: "#10B981" }} />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {insight.warm_opener && (
            <div className="p-2 rounded"
                 style={{ background: "#FFFBEB", border: "2px dashed #FFC145" }}>
              <div className="rm-label mb-0.5" style={{ color: "#92400E" }}>DM-able opener</div>
              <p className="text-sm">{insight.warm_opener}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3">
          {err && <div className="text-xs mb-2" style={{ color: "#991B1B" }}>{err}</div>}
          <button
            onClick={analyze}
            disabled={analyzing}
            className="rm-btn rm-btn-secondary text-xs px-3 py-2"
            data-testid={`contact-analyze-${contact.id}`}
          >
            {analyzing ? <Loader size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {analyzing ? "Resmo is thinking…" : "Why does this person matter? (10 credits)"}
          </button>
        </div>
      )}

      {/* Footer action row */}
      <div className="flex flex-wrap gap-2 mt-3 pt-3" style={{ borderTop: "1px dashed #E2E8F0" }}>
        <button
          onClick={markTouched}
          disabled={marking}
          className="rm-btn text-[11px] px-2 py-1"
          style={{
            background: tint.level === "fresh" ? "#ECFDF5" : "white",
            color: "#0F1B2D",
            border: "2px solid #0F1B2D",
            fontWeight: 700,
          }}
          data-testid={`contact-touched-${contact.id}`}
          title="Stamp this contact as touched today — keeps your stones green"
        >
          {marking ? <Loader size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
          Touched today
        </button>
        {insight && (
          <button
            onClick={() => onJumpTo?.("sparks")}
            className="rm-btn rm-btn-ghost text-[11px] px-2 py-1"
            data-testid={`contact-sparks-link-${contact.id}`}
          >
            Conversation sparks <ArrowRight size={11} />
          </button>
        )}
      </div>
    </div>
  );
}
